import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';

import OptionsButtonComponent from 'webapp/components/options-button';
import ToolTipComponent from 'webapp/components/tool-tip';

import type ConnectSelection from 'webapp/models-custom/connect-selection';
import type PopupModal from 'webapp/models-custom/popup/modal';
import type SourceType from 'webapp/models-custom/source-type';

interface Args {
	model: ConnectSelection;
}

type Action = 'connectSource' | 'deleteConnectSelection';
export type Option = PopupListItem<Action>;

export default class DataManagerConnectedTableRowSelectedComponent extends Component<Args> {
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('connect') declare connectService: Service.Connect;
	@service('navigation') declare navigationService: Service.Navigation;

	@tracked submitInProgress = false;

	get model(): ConnectSelection {
		return this.args.model;
	}

	get options(): Option[] {
		const options: Option[] = [];

		options.push({
			icon: 'Connect',
			text: 'Connect',
			action: 'connectSource',
		});

		options.push({
			class: 'delete',
			text: 'Delete request',
			action: 'deleteConnectSelection',
			icon: 'Trash',
		});

		return options;
	}

	@action
	doOptionsAction(actionName: string): void {
		this[actionName as Action]();
	}

	@action
	connectSource(): void {
		let popup: PopupModal | null = null;
		const sourceType = this.model.sourceType!;
		const onConnectSuccess = this.connectService.getAfterConnectAction();
		const connectNew = (sourceType: SourceType) => {
			void sourceType.connectNew({ origin: 'connected_selected' }).then((result) => {
				if (result.isSuccess) {
					onConnectSuccess(sourceType, +result.source.id, result.metadata);
					void popup?.close();
				}
			});
		};

		if (sourceType?.isGroup) {
			popup = this.mmSourceTypesService.openSourceTypeInfoPopup(sourceType, (sourceType) => {
				connectNew(sourceType);
			});
		} else {
			connectNew(sourceType);
		}
	}

	@action
	deleteConnectSelection(): void {
		if (this.submitInProgress) {
			return;
		}

		this.submitInProgress = true;
		const layer = this.infolayerService.loading('Deleting request for connect...');
		this.requestService
			.api('DELETE', `/connect/selection/${this.model.sourceType?.id}`)
			.then(
				() => {
					layer.ok('Request for connect successfully removed!');
				},
				() => {
					layer.close();
				},
			)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService))
			.finally(() => {
				this.submitInProgress = false;
			});
	}
	<template>
		<tr class="table-row selected-source filter-all">
			<td class="c0 no-grow first">
				<img src={{this.model.sourceType.avatar}} alt />
			</td>
			<td class="c1">
				<strong>
					{{this.model.sourceType.name}}
				</strong>

				<div>
					<span>Selected</span>
					<ToolTipComponent @text="Selected tools from your account setup step" />
				</div>
			</td>
			<td class="prio-low" data-label="Connection: ">
				N/A
			</td>
			<td class="perm prio-low" data-label="Permissions: ">
				N/A
			</td>
			<td class="prio-medium" data-label="Sync: ">
				N/A
			</td>
			<td class="prio-medium" data-label="Status: ">
				N/A
			</td>
			<td class="metrics prio-medium" data-label="Available metrics: ">
				N/A
			</td>
			<td class="c6 button-ct last">
				<OptionsButtonComponent
					@options={{this.options}}
					@theme="green-outlined"
					@onAction={{this.doOptionsAction}}
				/>
			</td>

		</tr>
	</template>
}
